import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useCancelExchange } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

export const CancelExchangeDialog = ({
  isOpen,
  setIsOpen,
  onActionDone,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onActionDone: () => void;
}) => {
  return (
    <Dialog
      title={<Trans id="article.actions.exchange_article.cancel.title">Cancel exchange</Trans>}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <CancelExchangeDialogContent onActionDone={onActionDone} onClose={() => setIsOpen(false)} />
    </Dialog>
  );
};

const CancelExchangeDialogContent = ({
  onActionDone,
  onClose,
}: {
  onActionDone: () => void;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { article } = useArticleContext();

  const [comment, setComment] = useState<string | undefined>();
  const [apiError, setApiError] = useState<string | null>(null);

  const { mutateAsync: cancelExchange, isPending: isCancelExchangePending } = useCancelExchange({
    articleId: article.id,
  });

  const submit = async () => {
    try {
      await cancelExchange({ comment });
      onClose();
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Paragraph color="secondary">
            <Trans id="article.actions.exchange_article.cancel.text">
              You're about to cancel the exchange process. Please specify the reason:
            </Trans>
          </Paragraph>
          <TextArea
            label={
              <>
                <Trans id="article.actions.exchange_article.cancel.comment.label">Comment</Trans>
                <OptionalLabel />
              </>
            }
            placeholder={t({
              id: 'article.actions.exchange_article.cancel.comment.placeholder',
              message: 'Write a comment...',
            })}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </main>

      <Footer>
        {!!apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <Button
            variant="danger"
            size="medium"
            onPress={submit}
            isLoading={isCancelExchangePending}
          >
            <Trans id="article.actions.exchange_article.cancel.button">Cancel exchange</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
